// src/services/videos.js
// Service helper to fetch videos from Supabase `videos` table, filtered by center & type.

import { createClient } from '@supabase/supabase-js';

// Use the same Supabase credentials as lib/supabase.js
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://dlmcxxzohervnbixauhs.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRsbWN4eHpvaGVydm5iaXhhdWhzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUxNzcwNzEsImV4cCI6MjA2MDc1MzA3MX0.UIQ7Wqo3R9aTYHYQHTFsHWGdhFFKLHjhEShlz2J2J-Q';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

/**
 * Fetch videos for a given center & type, in reverse chronological order.
 *
 * @param {string} centerId UUID of the center
 * @param {'short'|'regular'|'live'} type Video type
 * @returns {Promise<Array>} Rows from `videos` table
 */
export async function fetchVideos(centerId, type) {
  const { data, error } = await supabase
    .from('videos')
    .select('*')
    .eq('center_id', centerId)
    .eq('video_type', type)
    .order('published_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Fetch center ID by domain
 * 
 * @param {string} domain The domain to look up
 * @returns {Promise<string|null>} The center ID or null if not found
 */
export async function getCenterIdByDomain(domain) {
  console.log('[DEBUG videos.js] getCenterIdByDomain called with domain:', domain);
  
  // Normalize the input domain by stripping protocol and trailing slash
  const normalizedDomain = domain
    .replace(/^https?:\/\//, '')  // Remove protocol
    .replace(/\/$/, '');           // Remove trailing slash
    
  console.log('[DEBUG videos.js] Normalized input domain:', normalizedDomain);
  
  // First, try the normalized domain (this should match the database format)
  // Use .limit(1).single() to handle potential duplicates
  const { data: normalizedMatch, error: normalizedError } = await supabase
    .from('centers')
    .select('id')
    .eq('domain', normalizedDomain)
    .limit(1)
    .single();
    
  console.log('[DEBUG videos.js] Normalized domain query result:', { normalizedDomain, data: normalizedMatch, error: normalizedError });
  
  if (normalizedMatch) {
    console.log('[DEBUG videos.js] Found center ID with normalized domain:', normalizedMatch.id);
    return normalizedMatch.id;
  }
  
  // If not found, try the exact domain as provided (backward compatibility)
  const { data: exactMatch, error: exactError } = await supabase
    .from('centers')
    .select('id')
    .eq('domain', domain)
    .limit(1)
    .single();
    
  console.log('[DEBUG videos.js] Exact domain query result:', { domain, data: exactMatch, error: exactError });
  
  if (exactMatch) {
    console.log('[DEBUG videos.js] Found center ID with exact match:', exactMatch.id);
    return exactMatch.id;
  }
  
  // If still not found, try URL variations (for backward compatibility)
  const urlVariations = [
    `https://${normalizedDomain}`,
    `https://${normalizedDomain}/`,
    `http://${normalizedDomain}`,
    `http://${normalizedDomain}/`
  ];
  
  console.log('[DEBUG videos.js] Trying URL variations:', urlVariations);
  
  for (const variant of urlVariations) {
    const { data: variantData } = await supabase
      .from('centers')
      .select('id')
      .eq('domain', variant)
      .limit(1)
      .single();
      
    if (variantData) {
      console.log('[DEBUG videos.js] Found center ID:', variantData.id, 'for variant:', variant);
      return variantData.id;
    }
  }
  
  console.error('[DEBUG videos.js] No center found for any domain variation');
  return null;
}

/**
 * Fetch shorts with pagination for a given center
 * 
 * @param {string} centerId UUID of the center
 * @param {number} offset Starting position for pagination
 * @param {number} limit Number of videos to fetch (default 20)
 * @returns {Promise<{videos: Array, hasMore: boolean, totalCount: number}>}
 */
export async function fetchShorts(centerId, offset = 0, limit = 20) {
  console.log('[DEBUG videos.js] fetchShorts called with:', { centerId, offset, limit });
  
  try {
    // First get total count
    const { count, error: countError } = await supabase
      .from('videos')
      .select('*', { count: 'exact', head: true })
      .eq('center_id', centerId)
      .eq('video_type', 'short')
      .eq('privacy_status', 'public');

    console.log('[DEBUG videos.js] Count query result:', { count, countError });
    if (countError) throw countError;

    // Then fetch the videos
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('center_id', centerId)
      .eq('video_type', 'short')
      .eq('privacy_status', 'public')
      .order('published_at', { ascending: false })
      .range(offset, offset + limit - 1);

    console.log('[DEBUG videos.js] Videos query result:', { 
      dataLength: data?.length || 0, 
      error,
      firstVideo: data?.[0] 
    });
    
    if (error) throw error;

    return {
      videos: data || [],
      hasMore: offset + limit < count,
      totalCount: count
    };
  } catch (error) {
    console.error('[DEBUG videos.js] Error fetching shorts:', error);
    throw error;
  }
}
